import React, { useState, useEffect, useRef } from 'react';
import { useSocket } from '../contexts/SocketContext';

async function sha256(message: string): Promise<string> {
    const msgBuffer  = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray  = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

function hashToCrashPoint(hash: string, houseEdge = 0.03): number {
    const h  = parseInt(hash.slice(0, 8), 16);
    const e  = Math.pow(2, 32);
    const cp = Math.floor((100 * e - h) / (e - h)) / 100;
    return Math.max(1, Math.floor(cp * (1 - houseEdge) * 100) / 100);
}

const ProvablyFair: React.FC = () => {
    const { gameInfo, provablyFair } = useSocket();
    const [isExpanded, setIsExpanded]         = useState(false);
    const [verificationResult, setVerResult]  = useState<{
        isValid: boolean; calculatedCrashPoint: number; hashMatch: boolean; gameHash: string;
    } | null>(null);
    const [isVerifying, setIsVerifying] = useState(false);
    const currentVerificationRound = useRef(provablyFair.gameNumber);
    currentVerificationRound.current = provablyFair.gameNumber;
    useEffect(() => { setVerResult(null); setIsVerifying(false); }, [provablyFair.gameNumber]);

    const handleVerify = async () => {
        if (!provablyFair.serverSeed || !provablyFair.clientSeed) return;
        const verifyingRound = provablyFair.gameNumber;
        setIsVerifying(true);
        try {
            const calculatedHash = await sha256(provablyFair.serverSeed);
            const hashMatch      = calculatedHash === provablyFair.serverSeedHash;
            const combined       = `${provablyFair.serverSeed}:${provablyFair.clientSeed}`;
            const gameHash       = await sha256(combined);
            const calcCP         = hashToCrashPoint(gameHash);
            if (currentVerificationRound.current !== verifyingRound) return;
            setVerResult({
                isValid: hashMatch && Math.abs(calcCP - (provablyFair.crashPoint || 0)) < 0.01,
                calculatedCrashPoint: calcCP,
                hashMatch,
                gameHash,
            });
        } catch (err) { console.error('Verification error:', err); }
        if (currentVerificationRound.current === verifyingRound) setIsVerifying(false);
    };

    const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

    const truncate = (hash: string | null, len = 16) => {
        if (!hash) return '—';
        return hash.length <= len ? hash : `${hash.slice(0, len / 2)}…${hash.slice(-len / 2)}`;
    };

    const chainInfo       = provablyFair.chainInfo;
    const isHashChainMode = chainInfo?.mode === 'hashChain';

    const monoStyle: React.CSSProperties = {
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '0.75rem',
    };
    const codeBlockStyle: React.CSSProperties = {
        ...monoStyle,
        background: 'var(--s0)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-sm)',
        padding: '5px 8px',
        display: 'block',
        overflowX: 'hidden' as const,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
    };

    // Status badge
    const statusBadge = () => {
        const phase = gameInfo?.phase;
        if (provablyFair.serverSeedHash && (phase === 'STARTING' || phase === 'LAUNCHING' || phase === 'IN_PROGRESS')) {
            return <span style={{ ...monoStyle, background: 'rgba(240,180,41,0.1)', color: 'var(--gold)', border: '1px solid rgba(240,180,41,0.25)', borderRadius: 'var(--r-sm)', padding: '2px 8px' }}>Hash Committed</span>;
        }
        if (phase === 'CRASHED' && provablyFair.serverSeed) {
            return <span style={{ ...monoStyle, background: 'var(--green-dim)', color: 'var(--green)', border: '1px solid rgba(0,216,127,0.25)', borderRadius: 'var(--r-sm)', padding: '2px 8px' }}>Seed Revealed</span>;
        }
        return <span style={{ ...monoStyle, color: 'var(--text-muted)' }}>Waiting…</span>;
    };

    return (
        <div>
            {/* Header row */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
                aria-controls="fairness-details"
                style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', padding: 0 }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <svg width="16" height="16" fill="none" stroke="var(--green)" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>Provably Fair</span>
                        {provablyFair.gameNumber && (
                            <span style={{ ...monoStyle, color: 'var(--text-muted)' }}>#{provablyFair.gameNumber}</span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {statusBadge()}
                        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            style={{ color: 'var(--text-muted)', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>
            </button>

            {/* Expanded content */}
            {isExpanded && (
                <div id="fairness-details" style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <hr className="rule" />

                    {/* Hash chain info */}
                    {isHashChainMode && chainInfo && (
                        <div style={{ paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
                            <div className="flex items-center gap-2 mb-2">
                                <svg width="14" height="14" fill="none" stroke="#60a5fa" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                <span style={{ ...monoStyle, color: '#60a5fa', fontWeight: 600 }}>Hash Chain Active</span>
                            </div>
                            <div style={{ ...monoStyle, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                                <div className="flex justify-between">
                                    <span style={{ color: 'var(--text-muted)' }}>Terminating Hash:</span>
                                    <code style={{ color: '#60a5fa' }}>{truncate(chainInfo.terminatingHash, 16)}</code>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Server seed hash */}
                    {provablyFair.serverSeedHash && (
                        <div>
                            <div style={{ ...monoStyle, color: 'var(--text-muted)', marginBottom: 4 }}>Server Seed Hash (Commitment)</div>
                            <code style={{ ...codeBlockStyle, color: 'var(--gold)' }}>{truncate(provablyFair.serverSeedHash, 32)}</code>
                        </div>
                    )}

                    {/* Client seed */}
                    {provablyFair.clientSeed && (
                        <div>
                            <div style={{ ...monoStyle, color: 'var(--text-muted)', marginBottom: 4 }}>Client Seed (Game #{provablyFair.clientSeed})</div>
                            <code style={{ ...codeBlockStyle, color: '#c084fc' }}>{provablyFair.clientSeed}</code>
                        </div>
                    )}

                    {/* Revealed seeds (post-crash) */}
                    {provablyFair.serverSeed && (
                        <>
                            <div>
                                <div style={{ ...monoStyle, color: 'var(--text-muted)', marginBottom: 4 }}>Server Seed (Revealed)</div>
                                <div className="flex items-center gap-2">
                                    <code style={{ ...codeBlockStyle, color: 'var(--green)', flex: 1 }}>{truncate(provablyFair.serverSeed, 32)}</code>
                                    <button onClick={() => copyToClipboard(provablyFair.serverSeed!)}
                                        style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                                        title="Copy">
                                        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <div>
                                <div style={{ ...monoStyle, color: 'var(--text-muted)', marginBottom: 4 }}>Crash Point</div>
                                <div style={{ background: 'var(--s2)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '6px 10px', fontSize: '1.1rem', fontWeight: 700, color: 'var(--red)', fontFamily: "'Rajdhani', sans-serif" }}>
                                    {provablyFair.crashPoint?.toFixed(2)}x
                                </div>
                            </div>

                            <button
                                onClick={handleVerify}
                                disabled={isVerifying}
                                className="btn-primary btn-bet"
                                style={{ fontSize: '0.9rem', padding: '10px 16px' }}
                            >
                                {isVerifying ? (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                                        <svg style={{ animation: 'spin 1s linear infinite' }} width="14" height="14" fill="none" viewBox="0 0 24 24">
                                            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                            <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                                        </svg>
                                        Verifying…
                                    </span>
                                ) : 'Verify This Game'}
                            </button>

                            {verificationResult && (
                                <div style={{
                                    background: verificationResult.isValid ? 'rgba(0,216,127,0.06)' : 'rgba(255,64,64,0.06)',
                                    border: `1px solid ${verificationResult.isValid ? 'rgba(0,216,127,0.25)' : 'rgba(255,64,64,0.25)'}`,
                                    borderRadius: 'var(--r-md)',
                                    padding: '10px 12px',
                                }}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <svg width="16" height="16" fill={verificationResult.isValid ? 'var(--green)' : 'var(--red)'} viewBox="0 0 20 20">
                                            {verificationResult.isValid
                                                ? <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                                                : <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                                            }
                                        </svg>
                                        <span style={{ fontWeight: 600, fontSize: '0.85rem', color: verificationResult.isValid ? 'var(--green)' : 'var(--red)' }}>
                                            {verificationResult.isValid ? 'Game Verified!' : 'Verification Failed'}
                                        </span>
                                    </div>
                                    <div style={{ ...monoStyle, color: 'var(--text-secondary)', lineHeight: 2 }}>
                                        <div className="flex justify-between">
                                            <span style={{ color: 'var(--text-muted)' }}>Hash match:</span>
                                            <span style={{ color: verificationResult.hashMatch ? 'var(--green)' : 'var(--red)' }}>
                                                {verificationResult.hashMatch ? 'Yes' : 'No'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span style={{ color: 'var(--text-muted)' }}>Calculated crash:</span>
                                            <span>{verificationResult.calculatedCrashPoint.toFixed(2)}x</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span style={{ color: 'var(--text-muted)' }}>Actual crash:</span>
                                            <span>{provablyFair.crashPoint?.toFixed(2)}x</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Info text */}
                    <div style={{ ...monoStyle, color: 'var(--text-muted)', lineHeight: 1.8, paddingTop: 4, borderTop: '1px solid var(--border)' }}>
                        {isHashChainMode ? (
                            <>sha256(serverSeed) = commitment hash<br/>sha256(serverSeed:gameNumber) = crash point</>
                        ) : (
                            <>sha256(serverSeed) = commitment hash<br/>sha256(serverSeed:clientSeed) = crash point</>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProvablyFair;
